import { LOOP_SECONDS, VIDEO_SECONDS } from './constants'

export interface LoopRegion {
  /** Musical seconds. */
  start: number
  /** Musical seconds, exclusive. */
  end: number
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

/**
 * The single source of truth for musical time.
 *
 * Audio is the master clock: `AudioContext.currentTime` advances against the
 * same oscillator that clocks the samples out of the DAC, so a position derived
 * from it is exact by construction. Everything else in the player — the video
 * frame scheduler, the timeline, the readout — is a *consumer* of this clock and
 * never a second, competing clock. That is the whole basis of the sync guarantee.
 *
 * Two latencies matter and both were previously unaccounted for:
 *
 *  - **Output latency.** Audio scheduled at context time `T` is not *heard*
 *    until `T + outputLatency` — typically 10–40 ms, and much more over
 *    Bluetooth. So the sound reaching the listener right now is the sound that
 *    was scheduled `outputLatency` ago.
 *  - **Display latency.** A frame drawn now is not *seen* until the next
 *    compositor deadline, roughly one refresh interval later.
 *
 * Ignoring these leaves a systematic offset of tens of milliseconds where the
 * picture consistently leads the sound. `positionForDisplay()` corrects for
 * both, which is why it takes a lead time rather than reading "now".
 */
export class TransportClock {
  /** Manual A/V trim in seconds. Positive values show the picture later. */
  latencyTrim = 0

  private anchorContextTime = 0
  private anchorMusical = 0
  private pausedMusical = 0
  private playing = false
  private tempo = 1
  private loopEnabled = true
  private loop: LoopRegion = { start: 0, end: LOOP_SECONDS }

  constructor(private readonly ctx: AudioContext) {}

  /**
   * Best available estimate of how far the audio hardware runs behind the
   * scheduling clock. Chrome and Firefox report `outputLatency`; Safari does
   * not, so fall back to `baseLatency`, which at least captures the graph's own
   * buffering.
   */
  get outputLatency(): number {
    const ctx = this.ctx as AudioContext & { outputLatency?: number }
    const reported = typeof ctx.outputLatency === 'number' ? ctx.outputLatency : 0
    return (reported || ctx.baseLatency || 0) + this.latencyTrim
  }

  get isPlaying(): boolean {
    return this.playing
  }

  get currentTempo(): number {
    return this.tempo
  }

  get loopRegion(): Readonly<LoopRegion> {
    return this.loop
  }

  get isLooping(): boolean {
    return this.loopEnabled
  }

  /** Length of one lap in musical seconds. */
  get loopLength(): number {
    return Math.max(1 / 48_000, this.loop.end - this.loop.start)
  }

  /**
   * Anchor the clock. `contextTime` is when the audio sources were *scheduled*
   * to begin, and `musical` is the musical position they begin at — exactly the
   * pair handed to `AudioBufferSourceNode.start()`.
   */
  start(contextTime: number, musical: number): void {
    this.anchorContextTime = contextTime
    this.anchorMusical = musical
    this.playing = true
  }

  stop(): void {
    this.pausedMusical = this.positionAudible()
    this.playing = false
  }

  /** Move the playhead while stopped, or re-anchor an existing playback. */
  seek(musical: number): void {
    this.pausedMusical = this.constrain(musical)
    if (this.playing) {
      this.anchorMusical = this.pausedMusical
      this.anchorContextTime = this.ctx.currentTime
    }
  }

  /**
   * Change tempo without moving the playhead. The caller is responsible for
   * rebuilding the audio sources; this only re-anchors the maths so that the
   * musical position is continuous across the change.
   */
  setTempo(tempo: number, contextTime = this.ctx.currentTime): void {
    const here = this.playing ? this.musicalAt(contextTime) : this.pausedMusical
    this.tempo = tempo
    this.anchorMusical = here
    this.anchorContextTime = contextTime
    this.pausedMusical = here
  }

  setLoop(region: LoopRegion, enabled = this.loopEnabled): void {
    this.loop = region
    this.loopEnabled = enabled
    this.pausedMusical = this.constrain(this.pausedMusical)
  }

  setLoopEnabled(enabled: boolean): void {
    this.setLoop(this.loop, enabled)
  }

  /**
   * Musical position corresponding to an arbitrary point on the audio context
   * clock, wrapped into the loop region. This is the primitive; everything else
   * is a choice of which context time to ask about.
   */
  musicalAt(contextTime: number): number {
    if (!this.playing) return this.pausedMusical
    // Sources are scheduled a short lead ahead of `currentTime`, so this is
    // asked about instants before the anchor. Without the clamp the elapsed
    // time goes negative and wraps to just before the loop *end* — which would
    // flash the final frame of the loop for the whole scheduling lead.
    const elapsedWall = Math.max(0, contextTime - this.anchorContextTime)
    return this.constrain(this.anchorMusical + elapsedWall * this.tempo)
  }

  /**
   * The musical position currently reaching the listener's ears. Use this for
   * the timeline readout and for anything that should agree with what is heard.
   */
  positionAudible(): number {
    return this.musicalAt(this.ctx.currentTime - this.outputLatency)
  }

  /**
   * The musical position that should be *on screen* `leadSeconds` from now.
   * Pass the time remaining until the drawn frame is actually presented — from
   * `requestVideoFrameCallback`'s `expectedDisplayTime`, or one refresh interval
   * as an approximation.
   */
  positionForDisplay(leadSeconds: number): number {
    return this.musicalAt(this.ctx.currentTime + leadSeconds - this.outputLatency)
  }

  /**
   * Musical position for a moment expressed on the `performance.now()`
   * timeline, which is the clock `requestVideoFrameCallback` reports frame
   * display times against.
   *
   * The two timelines are correlated with `getOutputTimestamp()`, which returns
   * a matched (contextTime, performanceTime) pair sampled from the audio
   * hardware. Where it is unavailable the correlation falls back to a fixed
   * offset — less precise, but the fallback video path is the only caller and
   * it closes the remaining error with its own control loop.
   */
  musicalAtPerformanceTime(performanceMs: number): number {
    const ctxAtDisplay = this.contextTimeAtPerformanceTime(performanceMs)
    return this.musicalAt(ctxAtDisplay - this.outputLatency)
  }

  private contextTimeAtPerformanceTime(performanceMs: number): number {
    const ctx = this.ctx as AudioContext & {
      getOutputTimestamp?: () => { contextTime?: number; performanceTime?: number }
    }
    const stamp = ctx.getOutputTimestamp?.()
    if (stamp && typeof stamp.contextTime === 'number' && typeof stamp.performanceTime === 'number') {
      return stamp.contextTime + (performanceMs - stamp.performanceTime) / 1000
    }
    // Safari does not implement getOutputTimestamp; approximate by assuming the
    // two clocks advanced together since page load.
    return this.ctx.currentTime + (performanceMs - performance.now()) / 1000
  }

  /**
   * How many complete laps have elapsed since the anchor. Used to detect a loop
   * crossing without comparing wrapped positions, which is ambiguous.
   */
  lapAt(contextTime: number): number {
    if (!this.playing || !this.loopEnabled) return 0
    const elapsed = Math.max(0, contextTime - this.anchorContextTime) * this.tempo
    return Math.floor((this.anchorMusical - this.loop.start + elapsed) / this.loopLength)
  }

  /** Convert a musical position to an offset into a tempo-rendered buffer. */
  bufferOffsetFor(musical: number): number {
    return musical / this.tempo
  }

  private constrain(musical: number): number {
    if (!this.loopEnabled) return clamp(musical, 0, VIDEO_SECONDS)
    const len = this.loopLength
    const rel = (musical - this.loop.start) % len
    return this.loop.start + (rel < 0 ? rel + len : rel)
  }
}
